import axios from 'axios'

const WP_API_URL =
  import.meta.env.VITE_WORDPRESS_API_URL ||
  'https://wp.wedsy.co/wp-json/wp/v2'

export interface WPPost {
  id: number
  date: string
  modified: string
  slug: string
  status: 'publish' | 'draft' | 'pending'
  title: { rendered: string }
  content: { rendered: string; protected: boolean }
  excerpt: { rendered: string; protected: boolean }
  author: number
  featured_media: number
  categories: number[]
  tags: number[]
  reading_time?: number
  featured_image_url?: string | null
  author_details?: {
    id: number
    name: string
    bio: string
    avatar: string
    job_title?: string | null
    company?: string
    linkedin?: string | null
    twitter?: string | null
    github?: string | null
  }
  seo_meta?: {
    title: string | null
    description: string | null
    og_image: string | null
  }
  yoast_meta?: {
    title: string | null
    description: string | null
    og_image: string | null
    og_title: string | null
    og_description: string | null
    twitter_title: string | null
    twitter_description: string | null
    twitter_image: string | null
    canonical: string | null
  } | null
  _embedded?: {
    author?: Array<{ id: number; name: string; avatar_urls: Record<string, string> }>
    'wp:featuredmedia'?: Array<{
      id: number
      source_url: string
      alt_text: string
      media_details: { sizes: Record<string, { source_url: string }> }
    }>
    'wp:term'?: Array<Array<{ id: number; name: string; slug: string; taxonomy: string }>>
  }
}

export interface WPCategory {
  id: number
  name: string
  slug: string
  count: number
  description: string
}

const wpClient = axios.create({
  baseURL: WP_API_URL,
  headers: { 'Content-Type': 'application/json' },
})

export const wordpressApi = {
  async getPosts(params?: {
    page?: number
    per_page?: number
    categories?: number[]
  }): Promise<{ posts: WPPost[]; total: number; totalPages: number }> {
    const response = await wpClient.get<WPPost[]>('/posts', {
      params: {
        page: params?.page || 1,
        per_page: params?.per_page || 10,
        categories: params?.categories?.join(','),
        _embed: 'author,wp:featuredmedia,wp:term',
      },
    })
    return {
      posts: response.data,
      total: parseInt(response.headers['x-wp-total'] || '0', 10),
      totalPages: parseInt(response.headers['x-wp-totalpages'] || '0', 10),
    }
  },

  async getPostBySlug(slug: string): Promise<WPPost | null> {
    const response = await wpClient.get<WPPost[]>('/posts', {
      params: { slug, _embed: 'author,wp:featuredmedia,wp:term' },
    })
    return response.data[0] || null
  },

  async getCategories(): Promise<WPCategory[]> {
    const response = await wpClient.get<WPCategory[]>('/categories', {
      params: { per_page: 100, hide_empty: true },
    })
    return response.data
  },

  async getRecentPosts(excludeId?: number, limit = 3): Promise<WPPost[]> {
    const response = await wpClient.get<WPPost[]>('/posts', {
      params: { per_page: limit + 1, exclude: excludeId, _embed: 'author,wp:featuredmedia' },
    })
    return response.data.slice(0, limit)
  },
}
